import { Empty } from "antd";
import { createPortal } from "react-dom";
import { AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { faClose, faListUl, faSearch, faSort, IconDefinition } from "@fortawesome/free-solid-svg-icons";

//STYLES
import { ListaCompacta, Trigger } from "./style";

//APP
import { removerAcentos } from "../../../utils";

//COMPONENTS
import { Render } from "../render";
import Carregando from "../loading";

//PROPS
interface Props {
    /**Título exibido acima do valor selecionado */
    label?: string;
    /**Exibido ao lado do label */
    rule?: 'obrigatório' | 'opcional';
    /**Texto exibido quando nada está selecionado */
    empty_placeholder?: string;
    /**Ícone exibido antes do valor selecionado */
    icon?: IconDefinition;
    /**Descrição do item selecionado */
    value: string;
    onChange: React.Dispatch<React.SetStateAction<string>>;
    onChangeId?: React.Dispatch<React.SetStateAction<number | string>> | Function;
    items: any[];
    /**Chave do objeto que contém a descrição do item */
    descricaoItem: string;
    /**Chave do objeto que contém o id do item */
    idItem?: string;
    /**Chave do objeto exibida como informação secundária (tag) ao lado da descrição */
    extraItem?: string;
    /**Chave do objeto que contém a cor do texto da tag. Se omitido, usa a cor padrão */
    extraItemColorKey?: string;
    /**Chave do objeto que contém a cor de fundo da tag. Se omitido, usa a cor padrão */
    extraItemBgKey?: string;
    placeholder_busca?: string;
    /**Permite deselecionar o item ao clicar nele novamente */
    allowClear?: boolean;
    disabled?: boolean;
    loading?: boolean;
    width?: number | string;
    /**Largura da lista suspensa. Por padrão usa a largura convencional (450px) */
    widthList?: number;
    textTransform?: 'capitalize' | 'uppercase' | 'lowercase';
}

export const SelectCompacto: React.FC<Props> = ({ label, rule, value, onChange, onChangeId, items, descricaoItem,
    idItem, extraItem, extraItemColorKey, extraItemBgKey, empty_placeholder, placeholder_busca, icon,
    disabled, loading, width, widthList, textTransform, allowClear = true }) => {

    //REFs
    const refTrigger = useRef<HTMLButtonElement>(null);
    const refLista = useRef<HTMLDivElement>(null);

    //STATES
    const [open, setOpen] = useState<boolean>(false);
    const [buscar_na_lista, setBuscar_na_lista] = useState<string>('');
    const [posStyle, setPosStyle] = useState<React.CSSProperties>({});

    function fecharLista() {
        setOpen(false);
        setBuscar_na_lista('');
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const clicou_na_lista = refLista.current?.contains(event.target as Node);
            const clicou_no_trigger = refTrigger.current?.contains(event.target as Node);
            if (!clicou_na_lista && !clicou_no_trigger) {
                fecharLista();
            }
        };

        document.addEventListener('click', handleClickOutside, true);
        return () => document.removeEventListener('click', handleClickOutside, true);
    }, []);

    //POSICIONAMENTO DA LISTA
    const calcularPosicao = useCallback(() => {
        if (!open || !refTrigger.current) return;

        const rect_trigger = refTrigger.current.getBoundingClientRect();
        const altura_lista = refLista.current?.offsetHeight ?? 300;
        //LARGURA CONVENCIONAL DA LISTA (MESMA DO PopSelect)
        const largura_lista = widthList ?? 450;

        const margem = 5;

        //A LISTA COBRE O TRIGGER (NÃO ABRE ACIMA NEM ABAIXO DELE)
        let top: number = rect_trigger.top;
        let left: number = rect_trigger.left;

        //SE NÃO COUBER PARA BAIXO, SOBE O SUFICIENTE PARA CABER NA TELA
        if (top + altura_lista > window.innerHeight - margem) {
            top = window.innerHeight - altura_lista - margem;
        }
        //GARANTE QUE NÃO SAIA PELO TOPO
        if (top < margem) {
            top = margem;
        }
        //GARANTE QUE NÃO SAIA PELA DIREITA
        if (left + largura_lista > window.innerWidth) {
            left = window.innerWidth - largura_lista - 10;
        }
        //GARANTE QUE NÃO SAIA PELA ESQUERDA
        if (left < margem) {
            left = margem;
        }

        setPosStyle({ position: 'fixed', top, left, width: largura_lista, zIndex: 9999 });
    }, [open, widthList]);

    useLayoutEffect(() => {
        calcularPosicao();
    }, [open, calcularPosicao]);

    /**A lista só é medível depois de montada, então recalcula a posição
     * sempre que a altura dela mudar (montagem, busca, etc) */
    useEffect(() => {
        if (!open || !refLista.current) return;
        const observer = new ResizeObserver(() => calcularPosicao());
        observer.observe(refLista.current);
        return () => observer.disconnect();
    }, [open, calcularPosicao]);

    useEffect(() => {
        if (!open) return;
        const reposicionar = () => calcularPosicao();
        window.addEventListener('scroll', reposicionar, true);
        window.addEventListener('resize', reposicionar);
        return () => {
            window.removeEventListener('scroll', reposicionar, true);
            window.removeEventListener('resize', reposicionar);
        };
    }, [open, calcularPosicao]);

    function buscarItem(item_encontrar: string) {
        const chave_sem_acento = removerAcentos(item_encontrar);
        return function (item: any) {
            const item_sem_acento = removerAcentos(String(item?.[descricaoItem]));
            return item_sem_acento?.toLowerCase()?.includes(chave_sem_acento?.toLowerCase()) || !item_encontrar;
        }
    }

    const lista_filtrada = (items ?? []).filter(buscarItem(buscar_na_lista));

    const lista_suspensa = (
        <AnimatePresence>
            {open && (
                <ListaCompacta
                    ref={refLista}
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.97 }}
                    transition={{ duration: 0.15 }}
                    onClick={(e) => e.stopPropagation()}
                    style={posStyle}
                >
                    <div className="ListaCompacta__busca">
                        <FontAwesomeIcon icon={faSearch} className="ListaCompacta__busca__icon" />
                        <input
                            value={buscar_na_lista}
                            onChange={(e) => setBuscar_na_lista(e.currentTarget.value)}
                            placeholder={placeholder_busca ?? 'Buscar item na lista'}
                            autoFocus
                        />
                        {buscar_na_lista !== '' && (
                            <button className="ListaCompacta__busca__limpar" onClick={() => setBuscar_na_lista('')}>
                                <FontAwesomeIcon icon={faClose} />
                            </button>
                        )}
                    </div>

                    <Render condition={loading}>
                        <Carregando animation legenda="Carregando..." justifyContent="center" />
                    </Render>

                    <Render condition={!loading}>
                        <div className="ListaCompacta__list">
                            {lista_filtrada.map((item, index) => {
                                const descricao = item?.[descricaoItem];
                                const is_selecionado = value === descricao;
                                return (
                                    <button
                                        key={String(descricao) + index}
                                        className="ListaCompacta__list__item"
                                        data-active={is_selecionado}
                                        style={{ textTransform: textTransform }}
                                        title={String(descricao)}
                                        onClick={() => {
                                            if (is_selecionado && allowClear) {
                                                onChange('');
                                                onChangeId?.(null);
                                            } else {
                                                onChange(descricao);
                                                onChangeId?.(idItem ? item?.[idItem] : undefined);
                                            }
                                            fecharLista();
                                        }}
                                    >
                                        <div className="ListaCompacta__list__item__circle" />
                                        <span className="ListaCompacta__list__item__descr">{descricao}</span>

                                        {(!!extraItem && !!item?.[extraItem]) && (
                                            <span
                                                className="ListaCompacta__list__item__extra"
                                                style={{
                                                    color: extraItemColorKey ? item?.[extraItemColorKey] : undefined,
                                                    background: extraItemBgKey ? item?.[extraItemBgKey] : undefined
                                                }}
                                            >
                                                {item?.[extraItem]}
                                            </span>
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                    </Render>

                    <Render condition={!loading && lista_filtrada.length === 0}>
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhum registro encontrado" />
                    </Render>

                </ListaCompacta>
            )}
        </AnimatePresence>
    );

    return (
        <>
            <Trigger
                ref={refTrigger}
                type="button"
                whileTap={{ scale: disabled ? undefined : 0.98 }}
                onClick={disabled ? undefined : () => setOpen(!open)}
                data-disabled={disabled}
                style={{ width: width }}
                title={value || empty_placeholder}
            >
                <div className="Trigger__content">
                    {!!label && (
                        <span className="Trigger__content__label">
                            {label} {rule ? <small className="Trigger__content__label__rule">({rule})</small> : null}
                        </span>
                    )}

                    <div className="Trigger__content__selected" style={{ textTransform: value ? textTransform : undefined }}>
                        <FontAwesomeIcon icon={icon ?? faListUl} className="Trigger__content__selected__icon" />
                        {(value !== null && value !== '')
                            ? <span className="Trigger__content__selected__value">{value}</span>
                            : <span className="Trigger__content__selected__empty">{empty_placeholder ?? 'Nada foi selecionado'}</span>
                        }
                    </div>
                </div>

                <FontAwesomeIcon icon={faSort} className="Trigger__arrow" />
            </Trigger>

            {createPortal(lista_suspensa, document.body)}
        </>
    )
};
